import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  buildSamplingFormAnswerDraft,
  buildFormAnswersPayload,
  defaultEnvironmentalConditionDraft,
  fieldParameterDraftFromList,
  strapiGetBeritaAcaraSamplingForm,
  type EnvironmentalConditionDraft,
  type FieldParameterDraftState,
  type FormAnswerDraftState,
} from '../../../api/formSampling';
import {
  strapiFinishSurvey,
  strapiGetSamplingPlan,
  strapiListBeritaAcaraPreservationForms,
  strapiVerifyBeritaAcaraFormSampling,
  type BeritaAcaraPreservationFormRow,
  type StrapiFinishSurveyPayload,
  type StrapiPreservationGridRow,
} from '../../../api/strapiCrm';
import { useAuth } from '../../../auth/AuthContext';
import {
  isAmbientAirQualityJenisName,
  jenisPengukuranDocumentIdFromSamplingPlan,
  jenisPengukuranNameFromSamplingPlan,
} from '../../../sampling/samplingPlanBidang';
import { SurveyBeritaAcaraFormTabs } from '../../SurveyPage/components/SurveyBeritaAcaraFormTabs';
import {
  emptyFinishDraft,
  finishDraftFromLetterDetail,
  finishVehicleInformationPayload,
  finishMeasurementPayload,
  isFinishDraftLocationComplete,
  parseSamplingAirGroup,
  type FinishDraft,
} from '../../SurveyPage/components/surveyBeritaAcaraFinishTypes';
import { readCurrentPosition, resolveSurveyStartCoordinates } from '../../SurveyPage/surveyGeo';

type Props = {
  row: StrapiPreservationGridRow | null;
  readOnly?: boolean;
  onClose: () => void;
  onVerifiedChange: () => void | Promise<void>;
};

function beritaAcaraFormsQueryKey(jwt: string | null, workOrderDocumentId: string) {
  return ['strapi', 'beritaAcaraPreservationForms', jwt, workOrderDocumentId] as const;
}

function beritaAcaraListLabel(form: BeritaAcaraPreservationFormRow): string {
  return [form.sample_code || form.point_label, form.jenis_pengukuran_label]
    .filter((s) => Boolean(s?.trim()))
    .join(' | ');
}

function compareBySampleCode(a: BeritaAcaraPreservationFormRow, b: BeritaAcaraPreservationFormRow): number {
  const ao = a.list_order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.list_order ?? Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return a.point_index - b.point_index;
}

function BeritaAcaraVerifyPanel({
  form,
  workOrderDocumentId,
  readOnly = false,
  onVerifiedChange,
}: {
  form: BeritaAcaraPreservationFormRow;
  workOrderDocumentId: string;
  readOnly?: boolean;
  onVerifiedChange: () => void | Promise<void>;
}) {
  const { jwt } = useAuth();
  const qc = useQueryClient();
  const [finishDraft, setFinishDraft] = useState<FinishDraft>(emptyFinishDraft);
  const [sketchFile, setSketchFile] = useState<File | null>(null);
  const [formAnswerDraft, setFormAnswerDraft] = useState<FormAnswerDraftState>({});
  const [fieldParameterDraft, setFieldParameterDraft] = useState<FieldParameterDraftState>({});
  const [environmentalConditionDraft, setEnvironmentalConditionDraft] = useState<
    EnvironmentalConditionDraft[]
  >([]);
  const [formAnswerFiles, setFormAnswerFiles] = useState<Record<string, File>>({});
  const sketchPreviewRef = useRef('');
  sketchPreviewRef.current = finishDraft.location_sketsa_picture;
  const [activeTab, setActiveTab] = useState(0);
  const [optimisticVerified, setOptimisticVerified] = useState<boolean | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaved, setEditSaved] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);

  const samplingPlanQ = useQuery({
    queryKey: ['strapi', 'samplingPlan', jwt, form.sampling_plan_document_id],
    queryFn: () => strapiGetSamplingPlan(jwt!, form.sampling_plan_document_id),
    enabled: Boolean(jwt && form.sampling_plan_document_id),
  });
  const jenisPengukuranDocumentId = jenisPengukuranDocumentIdFromSamplingPlan(samplingPlanQ.data);

  const samplingFormQ = useQuery({
    queryKey: [
      'strapi',
      'beritaAcaraSamplingForm',
      jwt,
      form.berita_acara_document_id,
      jenisPengukuranDocumentId,
    ],
    queryFn: () =>
      strapiGetBeritaAcaraSamplingForm(jwt!, form.berita_acara_document_id, jenisPengukuranDocumentId),
    enabled: Boolean(jwt && form.berita_acara_document_id && samplingPlanQ.isSuccess),
  });

  useEffect(() => {
    return () => {
      const url = sketchPreviewRef.current;
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, []);

  useEffect(() => {
    if (!samplingFormQ.data) return;
    setFormAnswerDraft(
      buildSamplingFormAnswerDraft(
        samplingFormQ.data.form_groups ?? samplingFormQ.data.jenis_groups ?? [],
        samplingFormQ.data.existing_answers,
      ),
    );
    setFieldParameterDraft(fieldParameterDraftFromList(samplingFormQ.data.field_parameters));
    const savedEnvironment = samplingFormQ.data.environmental_condition;
    const ambient = isAmbientAirQualityJenisName(jenisPengukuranNameFromSamplingPlan(samplingPlanQ.data));
    setEnvironmentalConditionDraft(
      savedEnvironment.length
        ? savedEnvironment
        : ambient
          ? defaultEnvironmentalConditionDraft()
          : [],
    );
    setFinishDraft(finishDraftFromLetterDetail(samplingFormQ.data.letter_detail));
    setSketchFile(null);
    setFormAnswerFiles({});
  }, [samplingFormQ.data, samplingPlanQ.data]);

  useEffect(() => {
    setOptimisticVerified(null);
  }, [form.verified, form.berita_acara_document_id]);

  const verifyM = useMutation({
    mutationFn: async (verified: boolean) => {
      if (!jwt) throw new Error('Sesi berakhir.');
      return strapiVerifyBeritaAcaraFormSampling(jwt, form.berita_acara_document_id, verified);
    },
    onSuccess: async () => {
      setVerifyError(null);
      await qc.invalidateQueries({ queryKey: beritaAcaraFormsQueryKey(jwt, workOrderDocumentId) });
      await onVerifiedChange();
      setOptimisticVerified(null);
    },
    onError: (e) => {
      setOptimisticVerified(null);
      setVerifyError(e instanceof Error ? e.message : 'Gagal menyimpan verifikasi.');
    },
  });

  const editM = useMutation({
    mutationFn: async () => {
      if (!jwt) throw new Error('Sesi berakhir.');
      const form_answers = formGroups.length
        ? buildFormAnswersPayload(formGroups, formAnswerDraft, formAnswerFiles)
        : undefined;
      const geo = resolveSurveyStartCoordinates(finishDraft.longitude, finishDraft.latitude);
      const payload: StrapiFinishSurveyPayload = {
        complete: false,
        location_name: finishDraft.location_name.trim(),
        sample_point_collection: form.point_label.trim() || `Titik ${form.point_index}`,
        ...finishMeasurementPayload(finishDraft),
        jumlah_sampel: finishDraft.jumlah_sampel.trim(),
        is_having_blanko_wadah_penyerap: finishDraft.is_having_blanko_wadah_penyerap,
        abnormalitas_note: finishDraft.abnormalitas_note.trim(),
        keterangan_foto: finishDraft.keterangan_foto.trim(),
        ...(finishDraft.lhu_grouped_name ? { lhu_grouped_name: finishDraft.lhu_grouped_name } : {}),
        form_answers,
        jenis_pengukuran_document_id: jenisPengukuranDocumentId || undefined,
        ...(samplingFormQ.data
          ? {
              dynamic_main_parameter: {
                parameter_utama_lapangan: Object.fromEntries(
                  fieldParameters.map((param) => [
                    param.documentId,
                    (fieldParameterDraft[param.documentId] ?? '').trim(),
                  ]),
                ),
                environmental_condition: environmentalConditionDraft,
                ...(finishDraft.lhu_grouped_name === 'emisi_kendaraan'
                  ? { vehicle_information: finishVehicleInformationPayload(finishDraft) }
                  : {}),
              },
            }
          : {}),
        ...(geo ? { longitude: geo.longitude, latitude: geo.latitude } : {}),
        ...(sketchFile ? { location_sketsa_picture_file: sketchFile } : {}),
        ...(Object.keys(formAnswerFiles).length ? { form_answer_files: formAnswerFiles } : {}),
      };
      return strapiFinishSurvey(jwt, form.berita_acara_document_id, payload);
    },
    onSuccess: async () => {
      setEditError(null);
      setEditSaved(true);
      setSketchFile(null);
      setFormAnswerFiles({});
      await qc.invalidateQueries({
        queryKey: ['strapi', 'beritaAcaraSamplingForm', jwt, form.berita_acara_document_id],
      });
      await qc.invalidateQueries({ queryKey: beritaAcaraFormsQueryKey(jwt, workOrderDocumentId) });
      await onVerifiedChange();
    },
    onError: (e) => {
      setEditSaved(false);
      setEditError(e instanceof Error ? e.message : 'Gagal menyimpan perubahan.');
    },
  });

  const formGroups = samplingFormQ.data?.form_groups ?? samplingFormQ.data?.jenis_groups ?? [];
  const fieldParameters = samplingFormQ.data?.field_parameters ?? [];
  const showEnvironmentalCondition = isAmbientAirQualityJenisName(
    jenisPengukuranNameFromSamplingPlan(samplingPlanQ.data),
  );
  const formLoading = samplingPlanQ.isPending || samplingFormQ.isPending;
  const formError = samplingPlanQ.error ?? samplingFormQ.error;
  const airGroup = parseSamplingAirGroup(samplingFormQ.data?.letter_detail?.air_group);
  const shownVerified = optimisticVerified ?? form.verified;

  const onVerificationToggle = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value == null || verifyM.isPending) return;
    const next = value === 'true';
    if (next === shownVerified) return;
    setVerifyError(null);
    setOptimisticVerified(next);
    verifyM.mutate(next);
  };

  async function handleRetakeGps() {
    setGpsLoading(true);
    setEditError(null);
    setEditSaved(false);
    try {
      const geo = await readCurrentPosition();
      setFinishDraft((d) => ({
        ...d,
        longitude: String(geo.longitude),
        latitude: String(geo.latitude),
      }));
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Gagal mengambil GPS.');
    } finally {
      setGpsLoading(false);
    }
  }

  function saveEdits() {
    setEditSaved(false);
    if (!isFinishDraftLocationComplete(finishDraft, airGroup)) {
      setEditError('Nama lokasi dan parameter cuaca wajib diisi.');
      setActiveTab(finishDraft.location_name.trim() ? 1 : 0);
      return;
    }
    if (!finishDraft.lhu_grouped_name) {
      setEditError('Grup LHU wajib dipilih.');
      setActiveTab(0);
      return;
    }
    setEditError(null);
    editM.mutate();
  }

  return (
    <Stack spacing={2} sx={{ height: '100%', minHeight: 0, minWidth: 0 }}>
      <Box sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {beritaAcaraListLabel(form)}
        </Typography>
        {form.location_name ? (
          <Typography variant="body2" color="text.secondary">
            {form.location_name}
          </Typography>
        ) : null}
      </Box>

      {editError ? (
        <Alert severity="error" onClose={() => setEditError(null)} sx={{ flexShrink: 0 }}>
          {editError}
        </Alert>
      ) : null}
      {editSaved ? (
        <Alert severity="success" onClose={() => setEditSaved(false)} sx={{ flexShrink: 0 }}>
          Perubahan nilai tersimpan.
        </Alert>
      ) : null}
      {verifyError ? (
        <Alert severity="error" onClose={() => setVerifyError(null)} sx={{ flexShrink: 0 }}>
          {verifyError}
        </Alert>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', marginTop: { xs: '-10px !important', md: '16px !important'} }}>
        {formLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : formError ? (
          <Alert severity="warning">
            {formError instanceof Error ? formError.message : 'Gagal memuat form sampling.'}
          </Alert>
        ) : (
          <SurveyBeritaAcaraFormTabs
            formGroups={formGroups}
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            airGroup={airGroup}
            finishDraft={finishDraft}
            onFinishDraftChange={(next) => {
              setEditSaved(false);
              setFinishDraft(next);
            }}
            formAnswerDraft={formAnswerDraft}
            onFormAnswerDraftChange={(next) => {
              setEditSaved(false);
              setFormAnswerDraft(next);
            }}
            fieldParameters={fieldParameters}
            fieldParameterDraft={fieldParameterDraft}
            onFieldParameterDraftChange={(next) => {
              setEditSaved(false);
              setFieldParameterDraft(next);
            }}
            showEnvironmentalCondition={showEnvironmentalCondition}
            environmentalConditionDraft={environmentalConditionDraft}
            onEnvironmentalConditionDraftChange={(next) => {
              setEditSaved(false);
              setEnvironmentalConditionDraft(next);
            }}
            formAnswerFiles={formAnswerFiles}
            onFormAnswerFilesChange={(next) => {
              setEditSaved(false);
              setFormAnswerFiles(next);
            }}
            onSketchFileChange={(file) => {
              setEditSaved(false);
              setSketchFile(file);
            }}
            gpsLoading={gpsLoading}
            gpsDisabled={readOnly || editM.isPending || verifyM.isPending}
            onRetakeGps={readOnly ? undefined : () => void handleRetakeGps()}
            readOnly={readOnly}
          />
        )}
      </Box>

      {readOnly ? null : (
        <Stack spacing={1.5} sx={{ flexShrink: 0, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="contained"
            fullWidth
            data-testid="verify-form-sampling-save-values"
            disabled={editM.isPending || verifyM.isPending || formLoading || Boolean(formError) || gpsLoading}
            onClick={saveEdits}
          >
            {editM.isPending ? 'Menyimpan…' : 'Simpan perubahan'}
          </Button>
          <ToggleButtonGroup
            exclusive
            fullWidth
            color="primary"
            value={shownVerified ? 'true' : 'false'}
            disabled={verifyM.isPending || editM.isPending}
            onChange={onVerificationToggle}
            aria-label="Status verifikasi form sampling"
          >
            <ToggleButton value="true" data-testid="verify-form-sampling-verify">
              {verifyM.isPending && shownVerified ? 'Menyimpan…' : 'Verifikasi'}
            </ToggleButton>
            <ToggleButton value="false" data-testid="verify-form-sampling-unverify">
              {verifyM.isPending && !shownVerified ? 'Menyimpan…' : 'Non verifikasi'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
    </Stack>
  );
}

export function VerifyFormSamplingDialog({ row, readOnly = false, onClose, onVerifiedChange }: Props) {
  const { jwt } = useAuth();
  const workOrderDocumentId = row?.work_order_document_id ?? '';
  const [selectedId, setSelectedId] = useState('');

  const formsQ = useQuery({
    queryKey: beritaAcaraFormsQueryKey(jwt, workOrderDocumentId),
    queryFn: () => strapiListBeritaAcaraPreservationForms(jwt!, workOrderDocumentId),
    enabled: Boolean(jwt && workOrderDocumentId),
  });

  const forms = useMemo(
    () =>
      (formsQ.data ?? [])
        .filter((f) => f.is_fully_signed && Boolean(f.berita_acara_document_id))
        .sort(compareBySampleCode),
    [formsQ.data],
  );

  useEffect(() => {
    if (!row) {
      setSelectedId('');
      return;
    }
    if (!forms.length) return;
    if (!forms.some((f) => f.berita_acara_document_id === selectedId)) {
      setSelectedId(forms[0]!.berita_acara_document_id);
    }
  }, [row, forms, selectedId]);

  const selected = forms.find((f) => f.berita_acara_document_id === selectedId) ?? null;

  return (
    <Dialog
      open={Boolean(row)}
      onClose={onClose}
      fullScreen
      fullWidth
      slotProps={{ paper: { sx: { display: 'flex', flexDirection: 'column' } } }}
      data-testid="verify-form-sampling-dialog"
    >
      <DialogTitle>
        {readOnly ? 'Lihat Form Sampling' : 'Verifikasi Form Sampling'}
        {row ? ` — ${row.work_order_job_number || row.work_order_label || row.work_order_document_id}` : ''}
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, p: 0, minHeight: 0, overflow: 'hidden' }}>
        {formsQ.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : formsQ.error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              {formsQ.error instanceof Error ? formsQ.error.message : 'Gagal memuat berita acara.'}
            </Alert>
          </Box>
        ) : !forms.length ? (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary">Belum ada berita acara yang ditandatangani.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
              height: '100%',
              minHeight: 0,
            }}
          >
            <Box
              data-testid="verify-form-sampling-mobile-list"
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: 1,
                px: 1.5,
                py: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                WebkitOverflowScrolling: 'touch',
                padding: '10px 5px'
              }}
            >
              {forms.map((f) => {
                const isSelected = f.berita_acara_document_id === selectedId;
                return (
                  <Button
                    key={f.berita_acara_document_id}
                    size="small"
                    variant={isSelected ? 'contained' : 'outlined'}
                    color={isSelected ? 'primary' : 'inherit'}
                    aria-pressed={isSelected}
                    onClick={() => setSelectedId(f.berita_acara_document_id)}
                    sx={{
                      flex: '0 0 auto',
                      maxWidth: 280,
                      textTransform: 'none',
                      borderColor: 'divider',
                      justifyContent: 'flex-start',
                      padding: '7px 20px' 
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: '100%' }}
                    >
                      <Box
                        component="span"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {beritaAcaraListLabel(f)}
                      </Box>
                      <Chip
                        size="small"
                        color={f.verified ? 'success' : 'default'}
                        label={f.verified ? 'Verified' : 'Belum'}
                        sx={{
                          height: 20,
                          flexShrink: 0,
                          ...(isSelected ? { bgcolor: 'background.paper', color: 'text.primary' } : {}),
                        }}
                      />
                    </Box>
                  </Button>
                );
              })}
            </Box>
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                width: 320,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                overflow: 'auto',
              }}
            >
              <List dense disablePadding data-testid="verify-form-sampling-list">
                {forms.map((f) => (
                  <ListItemButton
                    key={f.berita_acara_document_id}
                    selected={f.berita_acara_document_id === selectedId}
                    onClick={() => setSelectedId(f.berita_acara_document_id)}
                  >
                    <ListItemText
                      primary={beritaAcaraListLabel(f)}
                      secondary={f.location_name || undefined}
                    />
                    <Chip
                      size="small"
                      color={f.verified ? 'success' : 'default'}
                      label={f.verified ? 'Verified' : 'Belum'}
                      sx={{ ml: 1, flexShrink: 0 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                width: { xs: '100%', md: 'unset' },
                margin: {xs: '10px 0', md: 'unset'},
                overflow: 'hidden',
                // p: { xs: 1.5, md: 2 },
                p: {xs: '0px 10px 0px', md: 2},
                display: 'flex',
                flexDirection: 'column',
                mx: 'auto',
              }}
            >
              {selected ? (
                <BeritaAcaraVerifyPanel
                  key={selected.berita_acara_document_id}
                  form={selected}
                  workOrderDocumentId={workOrderDocumentId}
                  readOnly={readOnly}
                  onVerifiedChange={onVerifiedChange}
                />
              ) : null}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Tutup</Button>
      </DialogActions>
    </Dialog>
  );
}
